import React, { useState, useMemo } from 'react';
import { trackCallConversion, trackLeadConversion } from '../../utils/tracking';
import {
  Zap,
  ShieldCheck,
  CheckCircle2,
  Clock,
  Wrench,
  Home,
  Building2,
  Lightbulb,
  Plug,
  Cpu,
  Phone,
  ArrowRight,
  Star,
  Check,
  AlertTriangle,
  FileText,
  ChevronDown,
  ChevronUp,
  X,
  Calculator,
  Sparkles,
  Award,
  CheckCircle,
  PhoneCall,
  Radio
} from 'lucide-react';
import './Electrician.css';

// 1. Service Categories (Design A Grid + Design B Categories)
const SERVICE_CATEGORIES = [
  {
    id: 'appliance-repair',
    title: 'Electrical Appliance Repair',
    category: 'repair',
    icon: Plug,
    tag: 'Popular',
    desc: 'Expert repair & installation of ceiling fans, geysers, exhaust fans, microwave points, and water pumps.',
    items: ['Ceiling Fan & Exhaust Fix', 'Geyser Installation & Thermostat Fix', 'Refrigerator & Washing Machine Power Point', 'Inverter & Battery Setup', 'Kitchen Power Outlets']
  },
  {
    id: 'emergency-repair',
    title: 'Emergency Short Circuit & Tripping',
    category: 'repair',
    icon: AlertTriangle,
    tag: '24/7 Express',
    desc: 'Rapid diagnosis and fix for short circuits, tripping MCBs, burnt wires, spark issues, and power blackouts.',
    items: ['MCB / RCCB Tripping Fix', 'Main Fuse & Switchboard Repair', 'Burned Line & Spark Fix', 'Phase Failure Troubleshooting', 'Emergency Power Restoration']
  },
  {
    id: 'house-contracting',
    title: 'New House Wiring Contracting',
    category: 'contracting',
    icon: Home,
    tag: 'Contractor Work',
    desc: 'Complete electrical contracting for newly built houses, villas, and floor extensions from pipe layout to final DB.',
    items: ['Concealed Conduit Piping', 'High-Grade Copper Wire Chasing', 'Distribution Board (DB) Panel Setup', 'Modular Switchboard Installation', '3-Phase Meter Connection Setup']
  },
  {
    id: 'commercial-contracting',
    title: 'Commercial & Office Contracting',
    category: 'contracting',
    icon: Building2,
    tag: 'Turnkey Contract',
    desc: 'Heavy-duty electrical installations for retail stores, corporate offices, warehouses, and industrial units.',
    items: ['3-Phase Power Distribution', 'Server Room & UPS Cabling', 'High-Bay Industrial Lighting', 'Electrical Safety Compliance Audit', 'Generators & Changeover Switches']
  },
  {
    id: 'lighting-installation',
    title: 'Modern Lighting & Chandelier Mounts',
    category: 'repair',
    icon: Lightbulb,
    tag: 'Design Upgrade',
    desc: 'Aesthetic LED lighting installation, COB spots, false ceiling ambient strips, and heavy chandelier mounting.',
    items: ['False Ceiling LED Strips & COB', 'Chandelier Mounting & Wiring', 'Outdoor & Landscape Lighting', 'Smart Dimmer Switch Setup', 'Track Lighting System']
  },
  {
    id: 'ev-inverter',
    title: 'EV Charger & Inverter Panel Setup',
    category: 'contracting',
    icon: Cpu,
    tag: 'Green Energy',
    desc: 'Professional installation of home EV fast chargers, solar hybrid inverters, and heavy-duty battery racks.',
    items: ['Level 2 EV Home Fast Charger', 'Solar Inverter Grid Interfacing', 'UPS Battery Bank Wiring', 'Surge Protection Device (SPD)', 'Load Management Systems']
  },
  {
    id: 'smart-home',
    title: 'Smart Home Automation & Security',
    category: 'repair',
    icon: Radio,
    tag: 'Smart Tech',
    desc: 'Transform your home with smart touch switches, video doorbells, smart locks, and sensor-based lighting controls.',
    items: ['Smart Wi-Fi Switch Modules', 'Video Doorbell & Intercom', 'Motion Sensor Lighting', 'Smart Lock Power Hookup', 'Central Home Hub Wiring']
  },
  {
    id: 'grounding-mdb',
    title: 'Earthing Pit & Main Panel (MDB)',
    category: 'contracting',
    icon: ShieldCheck,
    tag: 'Heavy Utility',
    desc: 'Chemical copper earthing pit construction, main distribution panel fabrication, and lightning arrestor setup.',
    items: ['Chemical Copper Earthing Pit', 'Main Distribution Board (MDB)', 'Lightning Protection System', 'Substation & Busbar Assembly', 'Thermal Imaging Safety Check']
  }
];

// 2. Interactive Calculator Pricing Data
const ESTIMATOR_DATA = {
  appliance: {
    label: 'Appliance Repair / Fix',
    options: [
      { name: 'Ceiling Fan Repair / Installation', unitPrice: 350, unit: 'fan(s)', estTime: '30-45 mins' },
      { name: 'Geyser Wiring & Switch Repair', unitPrice: 450, unit: 'geyser(s)', estTime: '45 mins' },
      { name: 'Modular Switchboard Repair (6-12 Module)', unitPrice: 300, unit: 'board(s)', estTime: '30 mins' },
      { name: 'Inverter & Battery Double Hookup', unitPrice: 750, unit: 'system(s)', estTime: '60 mins' },
      { name: 'Short Circuit / Tripping MCB Fix', unitPrice: 499, unit: 'issue(s)', estTime: '30-60 mins' }
    ]
  },
  contracting: {
    label: 'New Construction Wiring Contract',
    options: [
      { name: '1BHK Full Concealed House Wiring Contract', unitPrice: 18500, unit: 'contract', estTime: '3-5 Days' },
      { name: '2BHK Full Concealed House Wiring Contract', unitPrice: 32000, unit: 'contract', estTime: '6-8 Days' },
      { name: '3BHK / Duplex Full Wiring Contract', unitPrice: 48000, unit: 'contract', estTime: '8-12 Days' },
      { name: 'Independent Villa Turnkey Contracting', unitPrice: 75000, unit: 'contract', estTime: '12-18 Days' },
      { name: 'Commercial Office (per 1,000 sq ft)', unitPrice: 35000, unit: 'unit', estTime: '5-7 Days' }
    ]
  }
};

// 3. Contracting Workflow Steps
const WORKFLOW_STEPS = [
  {
    step: '01',
    title: 'Site Inspection & Blueprint Load Calculation',
    desc: 'Our senior electrical contractor inspects the architectural plan, evaluates power load requirements, and designs the conduit routing.'
  },
  {
    step: '02',
    title: 'Wall Chasing & PVC Conduit Piping',
    desc: 'Precision wall cutting and heavy-duty PVC conduit pipe embedding before plastering, keeping structure aesthetics intact.'
  },
  {
    step: '03',
    title: 'ISI Fire-Resistant Copper Wiring & DB Setup',
    desc: 'Pulling high-durability flame-retardant copper wires, installing MCB distribution boxes, and phase balancing.'
  },
  {
    step: '04',
    title: 'Switchboard Fitting, Load Testing & Handover',
    desc: 'Mounting designer modular switches, testing each point with thermal imaging & megger, and issuing 1-Year Guarantee Certificate.'
  }
];

// 4. Project Showcase Case Studies
const PROJECTS_SHOWCASE = [
  {
    id: 1,
    title: 'Luxury 4BHK Villa Turnkey Contracting',
    location: 'DLF Phase 5, Gurgaon',
    category: 'contracting',
    scope: 'Concealed Conduit Piping, 3-Phase DB Panel, Smart Automation & EV Charger Point',
    stats: '1,400m Wiring • 85 Points • 10-Day Execution',
    tag: 'Turnkey Contract'
  },
  {
    id: 2,
    title: 'Corporate Office Electrical Refit',
    location: 'Cyber City, Gurgaon',
    category: 'contracting',
    scope: '100+ Workstation Power Cabling, UPS Redundancy System & High-Bay Ambient Lighting',
    stats: '3-Phase Setup • Zero Downtime Cutover',
    tag: 'Commercial'
  },
  {
    id: 3,
    title: 'Emergency Main DB Panel & MCB Overhaul',
    location: 'Vasant Vihar, New Delhi',
    category: 'repair',
    scope: 'Replaced burnt main breaker, re-balanced phase loads, and restored power within 2 hours',
    stats: '2 Hour Emergency Response',
    tag: '24/7 Emergency'
  },
  {
    id: 4,
    title: 'Smart Home Automation & Lighting Setup',
    location: 'Noida Sector 62',
    category: 'repair',
    scope: 'Retrofitted 14 modular touch switchboards with Wi-Fi control and ambient LED COB spotlights',
    stats: '100% App Control • Energy Saving 30%',
    tag: 'Smart Home'
  }
];

// 5. Testimonials Data
const TESTIMONIALS = [
  {
    name: 'Rajesh Sharma',
    role: 'Homeowner (New 3BHK Construction)',
    rating: 5,
    comment: 'We hired DSMR Solutions as our electrical contractor for our new home in Gurgaon. From wall conduit chasing to main DB panel wiring, their team was extremely professional, neat, and used 100% genuine Havells fireproof wires. Delivered right on schedule!'
  },
  {
    name: 'Vikram Sethi',
    role: 'Commercial Property Manager',
    rating: 5,
    comment: 'When our office main breaker panel tripped and started burning, DSMR sent an emergency electrician within 35 minutes. They diagnosed the phase overload, replaced the MCB, and rebalanced the lines. Saved us from a massive operational shutdown.'
  },
  {
    name: 'Ananya Gupta',
    role: 'Interior Designer',
    rating: 5,
    comment: 'As an interior designer, I need top-tier electrical work for recessed lights, chandelier mounts, and smart switches. DSMR is my go-to contractor. Immaculate execution every single time.'
  }
];

// 6. FAQ Accordion Items
const FAQ_ITEMS = [
  {
    q: 'Do you handle both small electrical repairs and new house construction contracting?',
    a: 'Yes, absolutely! We have dedicated teams for both fast doorstep repairs (fans, geysers, switchboards, short circuits) and full turnkey electrical contracting for new homes, villas, offices, and building renovations.'
  },
  {
    q: 'How fast can an electrician arrive for emergency repair calls?',
    a: 'Our emergency doorstep technicians are stationed across Delhi NCR and reach your location within 30 to 60 minutes for urgent short circuits, MCB tripping, or power outages.'
  },
  {
    q: 'What is included in a New House Electrical Contracting project?',
    a: 'Our turnkey house wiring contract includes initial blueprint load planning, wall chasing, heavy-duty PVC conduit piping, fire-resistant copper wiring (Havells/Polycab), MCB distribution panel assembly, modular box fitting, earthing pit setup, load testing, and a 1-Year Guarantee Certificate.'
  },
  {
    q: 'Are your electricians certified and background checked?',
    a: 'Yes. All our electrical technicians and master contractors are licensed ITI/Diploma certified engineers with minimum 5+ years of field experience and 100% police background verification.'
  },
  {
    q: 'Do you provide a warranty on electrical repair & contracting work?',
    a: 'Yes! We offer a 30-day doorstep warranty on all appliance and fixture repair jobs, and a 1-Year Comprehensive Guarantee on new house contracting projects.'
  },
  {
    q: 'How are contracting prices estimated?',
    a: 'For new house wiring contracts, we offer upfront transparent pricing per point or lump-sum floor estimates (e.g. 2BHK, 3BHK, Villa). You can use our interactive estimator on this page or request a free on-site contractor visit for a customized quote.'
  }
];

export default function Electrician() {
  const [activeTab, setActiveTab] = useState('all');
  const [calcType, setCalcType] = useState('appliance');
  const [selectedOptionIdx, setSelectedOptionIdx] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [openFaq, setOpenFaq] = useState(0);
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [bookingService, setBookingService] = useState('General Electrical Repair');
  const [formSubmitted, setFormSubmitted] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    serviceType: 'Emergency Repair',
    address: '',
    date: '',
    notes: ''
  });

  // Service Filtering
  const filteredServices = useMemo(() => {
    if (activeTab === 'all') return SERVICE_CATEGORIES;
    return SERVICE_CATEGORIES.filter(s => s.category === activeTab);
  }, [activeTab]);

  // Estimator Calculations
  const currentCalcOptions = ESTIMATOR_DATA[calcType].options;
  const currentCalcItem = currentCalcOptions[selectedOptionIdx] || currentCalcOptions[0];
  const totalPrice = currentCalcItem.unitPrice * quantity;

  const handleOpenBooking = (serviceName = 'General Electrical Service') => {
    setBookingService(serviceName);
    setFormData(prev => ({ ...prev, serviceType: serviceName }));
    setIsBookingOpen(true);
    setFormSubmitted(false);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      mobileNumber: formData.phone || '',
      customerName: formData.name || 'Not provided',
      email: formData.email ? formData.email.trim() : null,
      address: formData.address || 'Not provided',
      deviceType: 'Electrical Service',
      brandModel: `Electrical - ${formData.serviceType || bookingService || 'General'}`,
      issueDescription: `Property: ${formData.propertyType || 'N/A'}. Notes: ${formData.notes || 'None'}`,
    };

    try {
      await fetch('https://dsmr-api.vercel.app/api/booking', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
    } catch (err) {
      console.warn('Vercel API error (Electrician):', err);
    } finally {
      setFormSubmitted(true);
      trackLeadConversion({
        mobileNumber: formData.phone,
        email: formData.email,
        device: 'Electrician',
        service: formData.serviceType,
      });
      setTimeout(() => {
        setIsBookingOpen(false);
        setFormSubmitted(false);
      }, 4000);
    }
  };

  return (
    <div className="electrician-page">
      {/* 1. HERO SECTION (Combines Design A & Design B Aesthetics) */}
      <section className="electrician-hero">
        <div className="hero-glow-overlay"></div>
        <div className="electrician-container hero-content-wrapper">
          <div className="hero-text-col">
            <div className="hero-badge">
              <Sparkles size={15} className="badge-spark-icon" />
              <span>LICENSED ELECTRICAL CONTRACTORS & DOORSTEP REPAIR</span>
            </div>

            <h1 className="hero-title">
              Trusted <span className="highlight-gold">Electrician</span> & Electrical Contracting
            </h1>

            <p className="hero-subtitle">
              From instant appliance repairs & short circuit fixes to complete new house wiring contracts,
              3-phase panel distribution, and smart home automation across Delhi NCR.
            </p>

            {/* Quick Benefits Bar */}
            <div className="hero-perks-grid">
              <div className="perk-pill">
                <Clock size={16} className="perk-icon" />
                <span>30-60 Min Rapid Arrival</span>
              </div>
              <div className="perk-pill">
                <ShieldCheck size={16} className="perk-icon" />
                <span>Licensed Master Electricians</span>
              </div>
              <div className="perk-pill">
                <CheckCircle2 size={16} className="perk-icon" />
                <span>Upfront Transparent Rates</span>
              </div>
              <div className="perk-pill">
                <Award size={16} className="perk-icon" />
                <span>1-Year Contract Warranty</span>
              </div>
            </div>

            <div className="hero-actions">
              <button
                className="elec-btn primary-btn"
                onClick={() => handleOpenBooking('Doorstep Electrician Visit')}
              >
                <Zap size={18} />
                <span>Book Electrician Now</span>
              </button>

              <a href="#estimator" className="elec-btn secondary-btn">
                <Calculator size={18} />
                <span>Estimate Contract Cost</span>
              </a>

              <a
                href="tel:+918700319769"
                className="elec-btn emergency-btn"
                data-dsmr-phone="true"
                onClick={(e) => trackCallConversion(e, "tel:+918700319769")}
              >
                <PhoneCall size={18} />
                <span>24/7 Emergency Line</span>
              </a>
            </div>
          </div>

          <div className="hero-image-col">
            <div className="hero-image-card">
              <img
                src="/images/electrician_hero.png"
                alt="Master Electrician Working on Electrical Breaker Panel"
                className="hero-main-img"
              />
              <div className="hero-floating-badge badge-top-right">
                <div className="floating-icon-box">
                  <Zap size={22} />
                </div>
                <div>
                  <span className="floating-title">2,500+ Repairs</span>
                  <span className="floating-sub">Completed with 100% Safety</span>
                </div>
              </div>

              <div className="hero-floating-badge badge-bottom-left">
                <div className="floating-icon-box gold">
                  <Home size={22} />
                </div>
                <div>
                  <span className="floating-title">350+ New House Contracts</span>
                  <span className="floating-sub">Full Turnkey Execution</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CERTIFIED PARTNERS (Light Mode Section) */}
      <section className="certified-partners-light">
        <div className="electrician-container">
          <p className="partners-title">CERTIFIED & AUTHORIZED INSTALLATION PARTNER</p>
          <div className="partners-logo-grid">
            <div className="partner-logo">Havells</div>
            <div className="partner-logo">Polycab</div>
            <div className="partner-logo">Finolex</div>
            <div className="partner-logo">Schneider</div>
            <div className="partner-logo">Legrand</div>
          </div>
        </div>
      </section>

      {/* 2. STATS BAR (Design B Style) */}
      <section className="elec-stats-bar">
        <div className="electrician-container stats-grid">
          <div className="stat-card">
            <span className="stat-num">2,500+</span>
            <span className="stat-label">Doorstep Repairs Done</span>
          </div>
          <div className="stat-card">
            <span className="stat-num">350+</span>
            <span className="stat-label">New House Wiring Contracts</span>
          </div>
          <div className="stat-card">
            <span className="stat-num">99.4%</span>
            <span className="stat-label">Safety Compliance Score</span>
          </div>
          <div className="stat-card">
            <span className="stat-num">4.9 ★</span>
            <span className="stat-label">Rating (1,800+ Reviews)</span>
          </div>
        </div>
      </section>

      {/* 3. DUAL CORE OFFERINGS: REPAIR vs CONTRACTING */}
      <section className="dual-offering-section">
        <div className="electrician-container">
          <div className="section-header center">
            <span className="section-tag">COMPREHENSIVE ELECTRICAL SOLUTIONS</span>
            <h2 className="section-title">What Electrical Work Do You Need Today?</h2>
            <p className="section-desc">
              Whether it’s a quick ceiling fan repair at your doorstep or end-to-end electrical contracting for a new house construction, we’ve got you covered.
            </p>
          </div>

          <div className="dual-cards-grid">
            {/* Card 1: Doorstep Appliance & Fixes */}
            <div className="dual-card repair-card">
              <div className="dual-card-media">
                <img src="/images/appliance_repair.png" alt="Appliance Repair & Fixes" />
                <span className="dual-card-badge">Doorstep Repair</span>
              </div>
              <div className="dual-card-body">
                <h3>Electrical Appliance & Home Fixes</h3>
                <p>
                  Fast, reliable repair and installation of household electrical devices, ceiling fans, geysers, switches, and short circuit troubleshooting.
                </p>
                <ul className="dual-feature-list">
                  <li><CheckCircle2 size={16} /> Ceiling Fan, Exhaust & Chandelier Mounting</li>
                  <li><CheckCircle2 size={16} /> Geyser Wiring, Thermostat & Element Fix</li>
                  <li><CheckCircle2 size={16} /> Short Circuit, Burnt Wire & Tripping MCB Fix</li>
                  <li><CheckCircle2 size={16} /> Modular Switchboard & Inverter Installation</li>
                </ul>
                <button
                  className="dual-action-btn"
                  onClick={() => handleOpenBooking('Doorstep Appliance Repair')}
                >
                  Book Doorstep Repair <ArrowRight size={16} />
                </button>
              </div>
            </div>

            {/* Card 2: New Construction & Contracting */}
            <div className="dual-card contracting-card">
              <div className="dual-card-media">
                <img src="/images/electrical_contractor.png" alt="New House Electrical Contracting" />
                <span className="dual-card-badge gold">Building Contractor</span>
              </div>
              <div className="dual-card-body">
                <h3>New Construction & Turnkey Contracting</h3>
                <p>
                  Full-scale electrical contracting work for newly constructed houses, villas, floors, and commercial buildings from conduit layout to final main panel.
                </p>
                <ul className="dual-feature-list">
                  <li><CheckCircle2 size={16} /> Concealed PVC Conduit Piping & Wall Chasing</li>
                  <li><CheckCircle2 size={16} /> High-Grade Fire-Resistant Copper Wiring (ISI)</li>
                  <li><CheckCircle2 size={16} /> Main Distribution Board (DB) & 3-Phase Meter Setup</li>
                  <li><CheckCircle2 size={16} /> Chemical Earthing Pit & EV Charger Installation</li>
                </ul>
                <button
                  className="dual-action-btn gold"
                  onClick={() => handleOpenBooking('New House Contracting Inspection')}
                >
                  Request Contractor Site Visit <ArrowRight size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. SERVICES CATEGORY GRID (Design A Grid Style) */}
      <section className="services-grid-section" id="services">
        <div className="electrician-container">
          <div className="section-header">
            <div>
              <span className="section-tag">ALL ELECTRICAL SERVICES</span>
              <h2 className="section-title">Explore Our Full Range of Electrical Services</h2>
            </div>

            {/* Filter Tabs */}
            <div className="filter-tabs-wrapper">
              <button
                className={`tab-btn ${activeTab === 'all' ? 'active' : ''}`}
                onClick={() => setActiveTab('all')}
              >
                All Services
              </button>
              <button
                className={`tab-btn ${activeTab === 'repair' ? 'active' : ''}`}
                onClick={() => setActiveTab('repair')}
              >
                Appliance & Home Fixes
              </button>
              <button
                className={`tab-btn ${activeTab === 'contracting' ? 'active' : ''}`}
                onClick={() => setActiveTab('contracting')}
              >
                Building Contracting
              </button>
            </div>
          </div>

          <div className="services-cards-grid">
            {filteredServices.map((service) => {
              const ServiceIcon = service.icon;
              return (
                <div className="service-card" key={service.id}>
                  <div className="service-card-top">
                    <div className="service-icon-box">
                      <ServiceIcon size={24} />
                    </div>
                    <span className={`service-tag-badge ${service.category === 'contracting' ? 'gold' : ''}`}>
                      {service.tag}
                    </span>
                  </div>

                  <h3 className="service-card-title">{service.title}</h3>
                  <p className="service-card-desc">{service.desc}</p>

                  <div className="service-subitems-list">
                    {service.items.map((item, idx) => (
                      <div className="subitem-row" key={idx}>
                        <Check size={14} className="subitem-check" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>

                  <button
                    className="service-card-btn"
                    onClick={() => handleOpenBooking(service.title)}
                  >
                    <span>Book Service</span>
                    <ArrowRight size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 5. INTERACTIVE COST ESTIMATOR WIDGET */}
      <section className="estimator-section" id="estimator">
        <div className="electrician-container">
          <div className="estimator-wrapper">
            <div className="estimator-info-col">
              <span className="section-tag">TRANSPARENT PRICING</span>
              <h2 className="section-title">Instant Electrical Cost Estimator</h2>
              <p className="section-desc">
                Calculate estimated pricing for appliance repair or full house wiring contract before booking. No surprise fees!
              </p>

              <div className="estimator-type-selector">
                <button
                  className={`type-btn ${calcType === 'appliance' ? 'active' : ''}`}
                  onClick={() => { setCalcType('appliance'); setSelectedOptionIdx(0); setQuantity(1); }}
                >
                  <Wrench size={16} />
                  <span>Appliance & Repairs</span>
                </button>
                <button
                  className={`type-btn ${calcType === 'contracting' ? 'active' : ''}`}
                  onClick={() => { setCalcType('contracting'); setSelectedOptionIdx(0); setQuantity(1); }}
                >
                  <Building2 size={16} />
                  <span>House Wiring Contracting</span>
                </button>
              </div>

              <div className="estimator-guarantees">
                <div className="guarantee-item">
                  <ShieldCheck size={18} />
                  <span>Fixed upfront pricing (inclusive of labor & diagnostic)</span>
                </div>
                <div className="guarantee-item">
                  <CheckCircle2 size={18} />
                  <span>100% Genuine Fire-Retardant Heavy Gauge Wires</span>
                </div>
              </div>
            </div>

            <div className="estimator-calc-col">
              <div className="calc-card">
                <div className="calc-header">
                  <h3>Select Service & Scope</h3>
                  <span className="calc-badge">{ESTIMATOR_DATA[calcType].label}</span>
                </div>

                <div className="calc-group">
                  <label>Select Specific Service Task:</label>
                  <select
                    value={selectedOptionIdx}
                    onChange={(e) => setSelectedOptionIdx(Number(e.target.value))}
                    className="calc-select"
                  >
                    {currentCalcOptions.map((opt, i) => (
                      <option key={i} value={i}>
                        {opt.name} — ₹{opt.unitPrice.toLocaleString('en-IN')} / {opt.unit}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="calc-group">
                  <label>Quantity / Number of Units ({currentCalcItem.unit}):</label>
                  <div className="quantity-controls">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="qty-btn"
                    >-</button>
                    <span className="qty-val">{quantity}</span>
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="qty-btn"
                    >+</button>
                  </div>
                </div>

                <div className="calc-result-box">
                  <div className="result-row">
                    <span className="result-label">Estimated Service Cost:</span>
                    <span className="result-amount">₹{totalPrice.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="result-details">
                    <span>⏱ Estimated Time: <strong>{currentCalcItem.estTime}</strong></span>
                    <span>📜 Warranty: <strong>{calcType === 'contracting' ? '1 Year Contract Warranty' : '30-Day Repair Warranty'}</strong></span>
                  </div>
                </div>

                <button
                  className="calc-submit-btn"
                  onClick={() => handleOpenBooking(`${currentCalcItem.name} (Qty: ${quantity}) - ₹${totalPrice.toLocaleString('en-IN')}`)}
                >
                  <span>Book This Estimate</span>
                  <ArrowRight size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6. WHY CHOOSE OUR ELECTRICIANS (Design B Showcase) */}
      <section className="why-choose-elec">
        <div className="electrician-container">
          <div className="section-header center">
            <span className="section-tag">OUR ADVANTAGE</span>
            <h2 className="section-title">Play It Safe & Choose DSMR Electricians</h2>
            <p className="section-desc">
              Electricity is no place for compromises. We adhere to rigorous safety protocols, high-grade materials, and certified electrical engineering standards.
            </p>
          </div>

          <div className="why-grid">
            <div className="why-card">
              <div className="why-icon-box">
                <Clock size={28} />
              </div>
              <h3>30-60 Min Rapid Arrival</h3>
              <p>Stationed technicians across Delhi NCR ensure swift arrival for urgent short circuits and repairs.</p>
            </div>

            <div className="why-card">
              <div className="why-icon-box">
                <ShieldCheck size={28} />
              </div>
              <h3>Licensed & Police Verified</h3>
              <p>Every contractor and electrician is background verified, ITI-certified, and safety-trained.</p>
            </div>

            <div className="why-card">
              <div className="why-icon-box">
                <Award size={28} />
              </div>
              <h3>100% Genuine ISI Spare Parts</h3>
              <p>We only install heavy-duty fire-resistant wires (Havells/Polycab) and Schneider/Legrand MCBs.</p>
            </div>

            <div className="why-card">
              <div className="why-icon-box">
                <FileText size={28} />
              </div>
              <h3>Clear Upfront Fixed Quotes</h3>
              <p>No hidden labor fees or unexpected surge charges. You get a transparent quote upfront.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 7. CONTRACTING WORKFLOW TIMELINE */}
      <section className="workflow-section">
        <div className="electrician-container">
          <div className="section-header center">
            <span className="section-tag">STEP-BY-STEP PROCESS</span>
            <h2 className="section-title">How Our New Building Contracting Works</h2>
            <p className="section-desc">
              From site load analysis to complete concealed pipe chasing, DB fabrication, and handover.
            </p>
          </div>

          <div className="workflow-timeline">
            {WORKFLOW_STEPS.map((step, idx) => (
              <div className="workflow-card" key={idx}>
                <span className="step-num">{step.step}</span>
                <h3 className="step-title">{step.title}</h3>
                <p className="step-desc">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 8. PROJECT SHOWCASE CASE STUDIES */}
      <section className="projects-showcase-section">
        <div className="electrician-container">
          <div className="section-header">
            <div>
              <span className="section-tag">PORTFOLIO & CASE STUDIES</span>
              <h2 className="section-title">Recent Electrical Contracting & Repair Projects</h2>
            </div>
          </div>

          <div className="projects-grid">
            {PROJECTS_SHOWCASE.map((proj) => (
              <div className="project-card" key={proj.id}>
                <div className="project-card-header">
                  <span className={`project-tag ${proj.category}`}>{proj.tag}</span>
                  <span className="project-loc">{proj.location}</span>
                </div>

                <h3 className="project-title">{proj.title}</h3>
                <p className="project-scope"><strong>Scope:</strong> {proj.scope}</p>
                <p className="project-stats">{proj.stats}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 9. TESTIMONIALS */}
      <section className="testimonials-elec-section">
        <div className="electrician-container">
          <div className="section-header center">
            <span className="section-tag">CLIENT FEEDBACK</span>
            <h2 className="section-title">What Our Homeowners & Builders Say</h2>
          </div>

          <div className="testimonials-grid">
            {TESTIMONIALS.map((t, idx) => (
              <div className="testi-card" key={idx}>
                <div className="testi-stars">
                  {[...Array(t.rating)].map((_, i) => (
                    <Star key={i} size={16} fill="#F5B93A" color="#F5B93A" />
                  ))}
                </div>
                <p className="testi-text">"{t.comment}"</p>
                <div className="testi-user">
                  <div className="user-avatar">{t.name.charAt(0)}</div>
                  <div>
                    <h4 className="user-name">{t.name}</h4>
                    <span className="user-role">{t.role}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 10. FAQ ACCORDION */}
      <section className="faq-elec-section">
        <div className="electrician-container">
          <div className="section-header center">
            <span className="section-tag">FREQUENTLY ASKED QUESTIONS</span>
            <h2 className="section-title">Got Questions About Electrical Repairs or Contracting?</h2>
          </div>

          <div className="faq-accordion-list">
            {FAQ_ITEMS.map((faq, idx) => {
              const isOpen = openFaq === idx;
              return (
                <div className={`faq-item ${isOpen ? 'open' : ''}`} key={idx}>
                  <button
                    className="faq-trigger"
                    onClick={() => setOpenFaq(isOpen ? -1 : idx)}
                  >
                    <span>{faq.q}</span>
                    {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </button>
                  {isOpen && (
                    <div className="faq-content">
                      <p>{faq.a}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 11. EMERGENCY CTA BANNER */}
      <section className="emergency-cta-banner">
        <div className="electrician-container cta-content-box">
          <div className="cta-text">
            <h2>Need Immediate Electrical Help or a Contracting Site Visit?</h2>
            <p>Our certified master electricians are available 24/7. Get doorstep response within 30-60 mins.</p>
          </div>
          <div className="cta-btns">
            <a
              href="tel:+918700319769"
              className="cta-call-btn"
              data-dsmr-phone="true"
              onClick={(e) => trackCallConversion(e, "tel:+918700319769")}
            >
              <Phone size={18} />
              <span>Call +91 8700319769</span>
            </a>
            <button
              className="cta-book-btn"
              onClick={() => handleOpenBooking('Emergency / Instant Contracting Inspection')}
            >
              <Zap size={18} />
              <span>Book Appointment</span>
            </button>
          </div>
        </div>
      </section>

      {/* 12. BOOKING INQUIRY MODAL */}
      {isBookingOpen && (
        <div className="booking-modal-overlay">
          <div className="booking-modal-card">
            <button className="close-modal-btn" onClick={() => setIsBookingOpen(false)}>
              <X size={20} />
            </button>

            {!formSubmitted ? (
              <>
                <div className="modal-header">
                  <div className="modal-icon-box">
                    <Zap size={22} />
                  </div>
                  <div>
                    <h3>Book Electrical Service</h3>
                    <p className="modal-sub">Selected Service: <strong>{bookingService}</strong></p>
                  </div>
                </div>

                <form className="modal-form" onSubmit={handleFormSubmit}>
                  <div className="form-group">
                    <label>Full Name *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Rahul Sharma"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label>Phone Number *</label>
                    <input
                      type="tel"
                      required
                      placeholder="e.g. 9876543210"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label>Service Type</label>
                    <select
                      value={formData.serviceType}
                      onChange={(e) => setFormData({ ...formData, serviceType: e.target.value })}
                    >
                      <option value="Doorstep Appliance Repair">Doorstep Appliance Repair (Fan, Geyser, Switch)</option>
                      <option value="Emergency Short Circuit / Tripping">Emergency Short Circuit / Tripping Fix</option>
                      <option value="New House Wiring Contracting">New House Wiring Contracting</option>
                      <option value="Commercial / Office Electrical Work">Commercial / Office Electrical Work</option>
                      <option value="Lighting & Chandelier Setup">Lighting & Chandelier Setup</option>
                      <option value="EV Charger / Inverter Installation">EV Charger / Inverter Installation</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Your Address / Location *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Sector 54, Gurgaon / Lajpat Nagar, Delhi"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Preferred Date</label>
                      <input
                        type="date"
                        value={formData.date}
                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Additional Notes / Specific Issues</label>
                    <textarea
                      rows="3"
                      placeholder="Describe the issue or contracting scope..."
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    ></textarea>
                  </div>

                  <button type="submit" className="submit-booking-btn">
                    <CheckCircle size={18} />
                    <span>Confirm Service Booking</span>
                  </button>
                </form>
              </>
            ) : (
              <div className="modal-success-state">
                <div className="success-icon-box">
                  <CheckCircle2 size={48} />
                </div>
                <h3>Booking Confirmed!</h3>
                <p>
                  Thank you, <strong>{formData.name}</strong>. Our senior electrician has been assigned. We will contact you at <strong>{formData.phone}</strong> within 10 minutes.
                </p>
                <div className="success-badge">Dispatching Technician Shortly ⚡</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
